/**
 * Navigation Configuration
 * Role-based navigation menu items for the sidebar.
 */

import type { UserRole } from '@/types/common.types';
import { ROUTES } from '@config/constants';

/**
 * Navigation item configuration.
 */
export interface NavItemConfig {
  id: string;
  label: string;
  path: string;
  icon: string;
  roles: UserRole[];
  children?: NavItemConfig[];
  /** When true, renders as a section divider with label (line + text), not a link */
  sectionLabel?: boolean;
}

/**
 * Navigation configuration for Daie users.
 * Also shown to managers (KDE/DPM) who are daie + manager.
 */
const DAIE_ROLES: UserRole[] = ['daie', 'manager'];
const DAIE_NAVIGATION: NavItemConfig[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'dashboard', roles: DAIE_ROLES },
  {
    id: 'sales-pipeline',
    label: 'Sales Pipeline',
    path: ROUTES.SALES_LEADS,
    icon: 'sales',
    roles: DAIE_ROLES,
    children: [
      { id: 'sales-leads', label: 'Leads', path: ROUTES.SALES_LEADS, icon: 'users', roles: DAIE_ROLES },
      { id: 'sales-appointments', label: 'Appointments', path: ROUTES.SALES_APPOINTMENTS, icon: 'calendar', roles: DAIE_ROLES },
      { id: 'sales-faraid', label: 'Faraid Calculator', path: ROUTES.SALES_FARAID, icon: 'chart', roles: DAIE_ROLES },
      { id: 'sales-closing', label: 'Closing Tools', path: ROUTES.SALES_CLOSING, icon: 'file', roles: DAIE_ROLES },
    ],
  },
  { id: 'quotation', label: 'Quotation', path: '/sales/quotations', icon: 'quotation', roles: DAIE_ROLES },
  {
    id: 'training',
    label: 'Training',
    path: '/training',
    icon: 'training',
    roles: DAIE_ROLES,
    children: [
      { id: 'online-training', label: 'Online Training', path: '/training', icon: 'training', roles: DAIE_ROLES },
      { id: 'physical-programs', label: 'Physical Programs', path: '/training/my-programs', icon: 'calendar', roles: DAIE_ROLES },
    ],
  },
  { id: 'notifications', label: 'Notifications', path: '/notifications', icon: 'notifications', roles: DAIE_ROLES },
  { id: 'profile', label: 'Profile', path: '/profile', icon: 'profile', roles: DAIE_ROLES },
];

/** Section divider shown before manager items when role is manager (daie + manager nav). */
const MANAGER_SECTION_LABEL: NavItemConfig = {
  id: 'manager-section',
  label: 'Manager',
  path: '',
  icon: '',
  roles: ['manager'],
  sectionLabel: true,
};

/**
 * Navigation configuration for Manager users.
 * Focused on team monitoring and KPI management.
 */
const MANAGER_NAVIGATION: NavItemConfig[] = [
  { id: 'overview', label: 'Overview', path: ROUTES.MANAGER_OVERVIEW, icon: 'dashboard', roles: ['manager'] },
  { id: 'agent-monitoring', label: 'Agent Monitoring', path: ROUTES.AGENT_MONITORING, icon: 'users', roles: ['manager'] },
  { id: 'kpi-compliance', label: 'KPI & Compliance', path: ROUTES.KPI_COMPLIANCE, icon: 'chart', roles: ['manager'] },
  { id: 'kpi-configuration', label: 'KPI Configuration', path: ROUTES.KPI_CONFIGURATION, icon: 'settings', roles: ['manager'] },
];

/**
 * Navigation configuration for Executive users.
 * Executive dashboard and report download only (manages all managers and daie).
 */
const EXECUTIVE_NAVIGATION: NavItemConfig[] = [
  { id: 'executive-dashboard', label: 'Executive Dashboard', path: ROUTES.EXECUTIVE_DASHBOARD, icon: 'dashboard', roles: ['executive'] },
  { id: 'report-download', label: 'Report Download', path: ROUTES.HQ_REPORT_DOWNLOAD, icon: 'download', roles: ['executive'] },
  { id: 'memo-center', label: 'Memo Center', path: ROUTES.MEMO, icon: 'mail', roles: ['executive'] },
];

/**
 * Navigation configuration for Admin users.
 * Full system administration access.
 */
const ADMIN_NAVIGATION: NavItemConfig[] = [
  { id: 'admin-dashboard', label: 'Dashboard', path: ROUTES.ADMIN_DASHBOARD, icon: 'dashboard', roles: ['admin'] },
  {
    id: 'configuration',
    label: 'Configuration',
    path: ROUTES.ROLES_MANAGEMENT,
    icon: 'settings',
    roles: ['admin'],
    children: [
      { id: 'roles-management', label: 'Roles & Permissions', path: ROUTES.ROLES_MANAGEMENT, icon: 'users', roles: ['admin'] },
      { id: 'document-templates', label: 'Document Templates', path: ROUTES.CONTRACT_TEMPLATES, icon: 'file', roles: ['admin'] },
      { id: 'product-config', label: 'Product Configurations', path: ROUTES.PRODUCT_CONFIG, icon: 'file', roles: ['admin'] },
    ],
  },
  { id: 'registration-management', label: 'Registration Management', path: '/registration/management', icon: 'link', roles: ['admin'] },
  { id: 'daie-management', label: "Dai'e Management", path: ROUTES.DAIE_MANAGEMENT, icon: 'users', roles: ['admin'] },
  { id: 'memo-blast', label: 'Memo Blast', path: '/memo', icon: 'mail', roles: ['admin'] },
  {
    id: 'reports',
    label: 'Reports',
    path: '/dashboard-report',
    icon: 'chart',
    roles: ['admin'],
    children: [
      { id: 'report-sales', label: 'Sales', path: '/dashboard-report/sales', icon: 'chart', roles: ['admin'] },
      { id: 'report-contract', label: 'Contract', path: '/dashboard-report/contract', icon: 'file', roles: ['admin'] },
      { id: 'report-training', label: 'Training', path: '/dashboard-report/training', icon: 'training', roles: ['admin'] },
      { id: 'report-export', label: 'Report Summary', path: '/dashboard-report/exportable-data', icon: 'download', roles: ['admin'] },
    ],
  },
  { id: 'incentives', label: 'Incentives', path: '/incentives', icon: 'gift', roles: ['admin'] },
  {
    id: 'training',
    label: 'Training',
    path: '/training/admin',
    icon: 'training',
    roles: ['admin'],
    children: [
      { id: 'online-training', label: 'Online Training', path: '/training/admin', icon: 'training', roles: ['admin'] },
      { id: 'physical-programs', label: 'Physical Programs', path: '/training/physical-programs', icon: 'calendar', roles: ['admin'] },
    ],
  },
  { id: 'support-inquiries', label: 'Support Inquiries', path: '/support-inquiries', icon: 'mail', roles: ['admin'] },
];

/**
 * Common navigation items shown to all roles.
 */
const COMMON_NAVIGATION: NavItemConfig[] = [
  { id: 'ai-assistant', label: 'AI Assistant', path: '/chatbot', icon: 'bot', roles: ['daie', 'manager', 'admin', 'executive'] },
];

/**
 * Gets navigation items for a specific user role.
 * Managers (KDE/DPM) get daie UI plus manager add-ons: Overview, Agent Monitoring, KPI & Compliance, KPI Configuration.
 */
export const getNavigationByRole = (role: UserRole): NavItemConfig[] => {
  let roleNavigation: NavItemConfig[] = [];

  switch (role) {
    case 'daie':
      roleNavigation = DAIE_NAVIGATION;
      break;
    case 'manager':
      roleNavigation = [...DAIE_NAVIGATION, MANAGER_SECTION_LABEL, ...MANAGER_NAVIGATION];
      break;
    case 'admin':
      roleNavigation = ADMIN_NAVIGATION;
      break;
    case 'executive':
      roleNavigation = EXECUTIVE_NAVIGATION;
      break;
    default:
      roleNavigation = [];
  }

  return [...roleNavigation, ...COMMON_NAVIGATION];
};

/**
 * Role display labels.
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  daie: "Dai'e Agent",
  manager: 'Manager',
  admin: 'Administrator',
  executive: 'Executive',
};

/**
 * Mock users for development mode.
 */
export const DEV_MOCK_USERS = {
  daie: {
    id: 1,
    name: 'Ahmad Rahman',
    email: 'ahmad@dreamsystem.com',
    role: 'daie' as UserRole,
    status: 'active' as const,
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
  },
  manager: {
    id: 2,
    name: 'Hassan Abdullah (KDE)',
    email: 'hassan@dreamsystem.com',
    role: 'manager' as UserRole,
    status: 'active' as const,
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
  },
  admin: {
    id: 3,
    name: 'Admin HQ',
    email: 'admin@dreamsystem.com',
    role: 'admin' as UserRole,
    status: 'active' as const,
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
  },
  executive: {
    id: 5,
    name: 'Executive HQ',
    email: 'executive@dreamsystem.com',
    role: 'executive' as UserRole,
    status: 'active' as const,
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
  },
};

